import React, { PropsWithChildren, useEffect } from "react";
import { styled, formGap, formPadding, sectionalMargin } from "@iventis/styles";
import { Theme } from "@emotion/react";

import { Body2, Header4 } from "@iventis/styles/src/components/texts";
import { useDispatch, useSelector } from "react-redux";
import { Content } from "@iventis/translations/content/typed-content";
import { useProject } from "@iventis/permissions/src/use-project";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useFunctionality } from "@iventis/utilities";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { toast } from "@iventis/toasts/src/toast";
import { AuthState, deleteProjectImage, deleteProjectLogo, getProjects, updateProjectDetails, uploadProjectImage, uploadProjectLogo } from "@iventis/permissions/src/auth.slice";
import { ThunkDispatch, Action } from "@reduxjs/toolkit";
import { NoAccessBanner } from "@iventis/permissions/src/upgrade-limit-overlay";
import { ProjectImageUploadComponent } from "./project-image-upload";
import { ProjectDetails } from "./project-details";

export const ProjectCustomisation: React.FC = () => {
    const translate = useIventisTranslate();

    const project = useProject();
    const dispatch = useDispatch<ThunkDispatch<unknown, typeof getProjects | typeof updateProjectDetails, Action<unknown>>>();

    const projects = useSelector((state: { auth: AuthState }) => state.auth.projects);

    useEffect(() => {
        if (projects.length === 0) {
            dispatch(getProjects({}));
        }
    }, []);

    const functionality = useFunctionality();

    return (
        <>
            <StyledContainer>
                <SettingSection>
                    <ProjectDetails />
                </SettingSection>
                <SettingSection>
                    {!functionality.customLogo && (
                        <StyledNoAccessBanner>
                            <NoAccessBanner upgradeType="customLogo" className="no-access-banner" />
                        </StyledNoAccessBanner>
                    )}
                    <Header4>{translate(Content.settings.projectCustomisation.projectLogoTitle)}</Header4>
                    <Body2 className="bottom-gap">{translate(Content.settings.projectCustomisation.projectLogoHint)}</Body2>
                    <ProjectImageUploadComponent
                        uploadThunk={uploadProjectLogo}
                        deleteThunk={deleteProjectLogo}
                        imageUrl={project?.logoUrl}
                        fileName={project?.logoFileName}
                        removeTitle={translate(Content.settings.projectCustomisation.projectLogoRemoveTitle)}
                        removeMessage={translate(Content.settings.projectCustomisation.projectLogoRemoveText)}
                        disabled={!functionality.customLogo}
                    />
                    <StyledHint>{translate(Content.settings.projectCustomisation.projectLogoRestrictionHint)}</StyledHint>
                </SettingSection>
                <SettingSection>
                    <Header4>{translate(Content.settings.projectCustomisation.projectImageTitle)}</Header4>
                    <Body2 className="bottom-gap">{translate(Content.settings.projectCustomisation.projectImageHint)}</Body2>
                    <ProjectImageUploadComponent
                        uploadThunk={uploadProjectImage}
                        deleteThunk={deleteProjectImage}
                        imageUrl={project?.imageUrl}
                        fileName={project?.imageFileName}
                        removeTitle={translate(Content.settings.projectCustomisation.projectImageRemoveTitle)}
                        removeMessage={translate(Content.settings.projectCustomisation.projectImageRemoveText)}
                        disabled={!functionality.customLogo}
                    />
                    <StyledHint>{translate(Content.settings.projectCustomisation.projectImageRestrictionHint)}</StyledHint>
                </SettingSection>
            </StyledContainer>
        </>
    );
};

const StyledHint = styled(Body2)`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    margin-top: ${formPadding};
`;

const StyledContainer = styled.div`
    flex-grow: 1;
    height: 100%;
    min-width: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
`;

const StyledNoAccessBanner = styled.div`
    .no-access-banner {
        width: 100%;
        margin: -${sectionalMargin} 0 ${sectionalMargin} -${sectionalMargin};
        width: calc(100% + ${sectionalMargin} + ${sectionalMargin});
    }
`;

const SettingSection: React.FC<PropsWithChildren> = ({ children }) => (
    <SettingsSectionContainer>
        <IventisErrorBoundaryWithToast toast={toast}>{children}</IventisErrorBoundaryWithToast>
    </SettingsSectionContainer>
);

const SettingsSectionContainer = styled.div`
    padding: ${sectionalMargin};
    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }: { theme: Theme }) => theme.otherColors.separatorDark};
    }
    h4 {
        margin-bottom: ${formGap};
    }
    .bottom-gap {
        margin-bottom: ${sectionalMargin};
    }
`;
